import { buildWireframeGroups, WireframeElementInfo, WireframeGroup, WireframeLabel } from '../utils/wireframe.js'

const STRUCTURAL_SELECTOR = [
  '.ae-design',
  '.ae-layout',
  '.ae-form',
  '.ae-form--layout',
  '.ae-form-section',
  '.ae-form-paragraph',
  '.ae-list',
  '.ae-control',
  '.ae-splitter',
  '.ae-tabs',
  '.ae-query'
].join(',')

const STYLE_ID = 'ae-wireframe-view'
const OVERLAY_CLASS = 'ae-wireframe-view'

export default {
  inject: ['getAeppicContext'],
  props: {
    target: {
      default: null
    },
    depth: {
      type: Number,
      default: 3
    }
  },
  data() {
    return {
      Aeppic: this.getAeppicContext('ae-wireframe-view'),
      groups: [],
      measuredElements: [],
      measuredElementMap: new Map(),
      targetRect: null,
      openGroupIds: new Set(),
      localDepth: this.depth,
      passthroughModifierKey: this.getPassthroughModifierKey(),
      passthroughMode: false,
      enabled: false,
      measureFrame: null,
      mutationObserver: null,
      removeDeveloperListener: null
    }
  },
  mounted() {
    this.enabled = this.isEnabled()
    this.installStyle()

    const listener = () => {
      this.enabled = this.isEnabled()
      this.openGroupIds.clear()
      this.scheduleMeasure()
    }

    this.Aeppic.Developer?.on?.('wireframe-mode-changed', listener)
    this.removeDeveloperListener = () => this.Aeppic.Developer?.off?.('wireframe-mode-changed', listener)

    window.addEventListener('resize', this.scheduleMeasure, { passive: true })
    window.addEventListener('scroll', this.scheduleMeasure, { passive: true, capture: true })
    window.addEventListener('keydown', this.onKeydown, true)
    window.addEventListener('keyup', this.onKeyup, true)
    window.addEventListener('pointermove', this.onPointerModifierChange, true)
    window.addEventListener('pointerdown', this.onPointerModifierChange, true)
    window.addEventListener('pointerup', this.onPointerModifierChange, true)
    window.addEventListener('contextmenu', this.onContextMenu, true)
    window.addEventListener('blur', this.disablePassthroughMode)
    document.addEventListener('visibilitychange', this.onVisibilityChange)

    this.setupMutationObserver()
    this.scheduleMeasure()
  },
  updated() {
    this.scheduleMeasure()
  },
  beforeDestroy() {
    this.removeDeveloperListener?.()
    window.removeEventListener('resize', this.scheduleMeasure)
    window.removeEventListener('scroll', this.scheduleMeasure, true)
    window.removeEventListener('keydown', this.onKeydown, true)
    window.removeEventListener('keyup', this.onKeyup, true)
    window.removeEventListener('pointermove', this.onPointerModifierChange, true)
    window.removeEventListener('pointerdown', this.onPointerModifierChange, true)
    window.removeEventListener('pointerup', this.onPointerModifierChange, true)
    window.removeEventListener('contextmenu', this.onContextMenu, true)
    window.removeEventListener('blur', this.disablePassthroughMode)
    document.removeEventListener('visibilitychange', this.onVisibilityChange)
    this.mutationObserver?.disconnect()

    if (this.measureFrame != null) {
      this.cancelFrame(this.measureFrame)
    }
  },
  watch: {
    target() {
      this.setupMutationObserver()
      this.scheduleMeasure()
    },
    depth() {
      this.localDepth = this.depth
      this.scheduleMeasure()
    }
  },
  methods: {
    isEnabled() {
      return this.Aeppic.Developer?.isEnabled === true && this.Aeppic.Developer?.wireframeMode === true
    },
    installStyle() {
      if (document.getElementById(STYLE_ID)) {
        return
      }

      const style = document.createElement('style')
      style.id = STYLE_ID
      style.textContent = `
.ae-wireframe-view {
  position: fixed;
  inset: 0;
  z-index: 2147483000;
  pointer-events: none;
  color: #0f172a;
  font-family: ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.ae-wireframe-view__svg {
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}
.ae-wireframe-view__group {
  pointer-events: auto;
}
.ae-wireframe-view__group--compact .ae-wireframe-view__label,
.ae-wireframe-view__group--compact .ae-wireframe-view__label-bg,
.ae-wireframe-view__group--compact .ae-wireframe-view__nav-action {
  opacity: 0;
  pointer-events: none;
}
.ae-wireframe-view__group--compact:hover .ae-wireframe-view__label,
.ae-wireframe-view__group--compact:hover .ae-wireframe-view__label-bg,
.ae-wireframe-view__group--compact:hover .ae-wireframe-view__nav-action {
  opacity: 1;
  pointer-events: all;
}
.ae-wireframe-view__hit {
  fill: transparent;
  pointer-events: all;
}
.ae-wireframe-view__box {
  fill: rgba(14, 165, 233, 0.045);
  stroke: rgba(14, 165, 233, 0.85);
  stroke-width: 1;
  vector-effect: non-scaling-stroke;
  transition: opacity 120ms ease, background 120ms ease;
}
.ae-wireframe-view__box--ancestor {
  opacity: 0.32;
}
.ae-wireframe-view__box--leaf,
.ae-wireframe-view__box:hover {
  opacity: 1;
}
.ae-wireframe-view__box--off-path {
  opacity: 0.26;
  stroke: rgba(100, 116, 139, 0.55);
  stroke-dasharray: 5 3;
  fill: rgba(255, 255, 255, 0.58);
}
.ae-wireframe-view__group:hover .ae-wireframe-view__box--off-path {
  opacity: 1;
  stroke: rgba(14, 165, 233, 0.85);
  stroke-dasharray: none;
  fill: rgba(14, 165, 233, 0.08);
}
.ae-wireframe-view__group:hover .ae-wireframe-view__box {
  fill: rgba(14, 165, 233, 0.12);
}
.ae-wireframe-view__label {
  font-size: 11px;
  dominant-baseline: hanging;
  fill: #0f172a;
  pointer-events: none;
  transition: opacity 120ms ease, background 120ms ease;
}
.ae-wireframe-view__label-bg {
  fill: rgba(255, 255, 255, 0.94);
  stroke: rgba(15, 23, 42, 0.25);
  stroke-width: 1;
  pointer-events: none;
  vector-effect: non-scaling-stroke;
  transition: opacity 120ms ease, fill 120ms ease;
}
.ae-wireframe-view__nav-action {
  cursor: pointer;
  pointer-events: all;
  transition: opacity 120ms ease;
}
.ae-wireframe-view__nav-action-bg {
  fill: transparent;
}
.ae-wireframe-view__nav-action:hover .ae-wireframe-view__nav-action-bg {
  fill: rgba(14, 165, 233, 0.12);
}
.ae-wireframe-view__nav-action-text {
  fill: #0f172a;
  font-size: 18px;
  font-weight: 700;
  dominant-baseline: middle;
  text-anchor: middle;
}
.ae-wireframe-view__label--ancestor {
  opacity: 0.42;
}
.ae-wireframe-view__label--leaf,
.ae-wireframe-view__group:hover .ae-wireframe-view__label {
  opacity: 1;
}
.ae-wireframe-view__label--off-path {
  opacity: 0.24;
  fill: rgba(15, 23, 42, 0.62);
}
.ae-wireframe-view__label-bg--off-path {
  fill: rgba(255, 255, 255, 0.48);
  stroke: rgba(100, 116, 139, 0.35);
}
.ae-wireframe-view__group:hover .ae-wireframe-view__label--off-path {
  opacity: 1;
  fill: #0f172a;
}
.ae-wireframe-view__group:hover .ae-wireframe-view__label-bg--off-path {
  fill: rgba(255, 255, 255, 0.94);
}
.ae-wireframe-view--passthrough .ae-wireframe-view__group,
.ae-wireframe-view--passthrough .ae-wireframe-view__hit {
  pointer-events: none;
}
`
      document.head.appendChild(style)
    },
    setupMutationObserver() {
      this.mutationObserver?.disconnect()

      const target = this.resolveTargetElement()
      if (!target || typeof MutationObserver === 'undefined') {
        return
      }

      this.mutationObserver = new MutationObserver(mutations => {
        if (mutations.every(mutation => this.isOverlayMutation(mutation))) {
          return
        }

        this.scheduleMeasure()
      })
      this.mutationObserver.observe(target, {
        attributes: true,
        childList: true,
        subtree: true
      })
    },
    scheduleMeasure() {
      if (this.measureFrame != null) {
        this.cancelFrame(this.measureFrame)
      }

      this.measureFrame = this.requestFrame(() => {
        this.measureFrame = null
        this.measure()
      })
    },
    requestFrame(callback: FrameRequestCallback) {
      if (typeof requestAnimationFrame === 'function') {
        return requestAnimationFrame(callback)
      }

      return setTimeout(() => callback(Date.now()), 0)
    },
    cancelFrame(frame: number) {
      if (typeof cancelAnimationFrame === 'function') {
        cancelAnimationFrame(frame)
      } else {
        clearTimeout(frame)
      }
    },
    measure() {
      if (!this.isEnabled()) {
        this.groups = []
        this.measuredElements = []
        this.measuredElementMap = new Map()
        this.targetRect = null
        return
      }

      const target = this.resolveTargetElement()
      if (!target) {
        this.groups = []
        this.measuredElements = []
        this.measuredElementMap = new Map()
        this.targetRect = null
        return
      }

      const targetRect = this.getTargetRect(target)
      const candidates = this.findCandidates(target)
      const elementIds = new Map<HTMLElement, string>()
      const measuredElementMap = new Map<string, HTMLElement>()
      const elements: WireframeElementInfo[] = []

      candidates.forEach((element, index) => elementIds.set(element, `wf-${index}`))

      for (const element of candidates) {
        const info = this.buildElementInfo(element)
        const rect = this.getMeasurementRect(element, info, targetRect)
        if (!this.isVisibleRect(rect, targetRect)) {
          continue
        }

        measuredElementMap.set(elementIds.get(element), element)
        const parent = this.findParentCandidate(element, elementIds)
        elements.push({
          id: elementIds.get(element),
          parentId: parent ? elementIds.get(parent) : null,
          rect: {
            top: rect.top - targetRect.top,
            left: rect.left - targetRect.left,
            width: rect.width,
            height: rect.height
          },
          label: this.buildLabel(info),
          meta: info
        })
      }

      this.targetRect = targetRect
      this.measuredElements = elements
      this.measuredElementMap = measuredElementMap
      this.rebuildGroups()
    },
    rebuildGroups() {
      this.groups = buildWireframeGroups(this.measuredElements as WireframeElementInfo[], {
        maxDepth: this.localDepth,
        openGroupIds: this.openGroupIds
      })
    },
    resolveTargetElement(): HTMLElement | null {
      const target = this.target || this.Aeppic.Developer?.wireframeTarget || 'page'

      if (target instanceof Element) {
        return target as HTMLElement
      }

      if (target?.$el instanceof Element) {
        return target.$el as HTMLElement
      }

      if (target === 'page') {
        return this.Aeppic.rootEl || document.querySelector('.ae-root') || document.body
      }

      if (target === 'app') {
        return document.querySelector('.ae-app') || this.Aeppic.rootEl || document.body
      }

      if (target === 'developer') {
        return document.querySelector('.ae-developer') || document.querySelector('.ae-block-b956d062-ba70-482d-b404-da5cec71afc6') || null
      }

      if (typeof target === 'string') {
        return document.querySelector(target)
      }

      return this.Aeppic.rootEl || document.body
    },
    getTargetRect(target: HTMLElement) {
      if (target === document.body || target === document.documentElement) {
        return {
          top: 0,
          left: 0,
          width: window.innerWidth,
          height: window.innerHeight
        }
      }

      return target.getBoundingClientRect()
    },
    findCandidates(target: HTMLElement) {
      const elements = Array.from(target.querySelectorAll(STRUCTURAL_SELECTOR)) as HTMLElement[]

      if (target.matches?.(STRUCTURAL_SELECTOR)) {
        elements.unshift(target)
      }

      return elements.filter(element => !element.closest(`.${OVERLAY_CLASS}`))
    },
    isOverlayMutation(mutation: MutationRecord) {
      const target = mutation.target as HTMLElement
      if (target.closest?.(`.${OVERLAY_CLASS}`)) {
        return true
      }

      const addedNodes = Array.from(mutation.addedNodes) as HTMLElement[]
      const removedNodes = Array.from(mutation.removedNodes) as HTMLElement[]
      const changedNodes = [...addedNodes, ...removedNodes]

      return changedNodes.length > 0 && changedNodes.every(node => {
        return node.classList?.contains(OVERLAY_CLASS) || node.closest?.(`.${OVERLAY_CLASS}`)
      })
    },
    findParentCandidate(element: HTMLElement, elementIds: Map<HTMLElement, string>) {
      let parent = element.parentElement

      while (parent) {
        if (elementIds.has(parent)) {
          return parent
        }

        parent = parent.parentElement
      }

      return null
    },
    buildElementInfo(element: HTMLElement) {
      return this.Aeppic.Developer?.buildWireframeElementInfo?.(element)
        ?? this.Aeppic.Developer?.buildInspectorDiscoveryPath?.([element])?.[0]
        ?? { type: 'Element', tagName: element.tagName?.toLowerCase() }
    },
    buildLabel(info): WireframeLabel {
      const id = info.layoutId || info.designId || info.formId || info.listId || info.controlId || info.documentId || null

      return {
        type: info.type || 'Element',
        name: info.componentName || info.documentTitle || info.formName || info.tagName || null,
        id,
        fieldName: info.fieldName || null,
        meta: info
      }
    },
    getMeasurementRect(element: HTMLElement, info, targetRect) {
      const rect = element.getBoundingClientRect()

      if (info?.type === 'Control') {
        return this.findControlContentRect(element, rect, targetRect) ?? rect
      }

      if (info?.type !== 'Design' || String(info?.componentName ?? '').toLowerCase() !== 'reference') {
        return rect
      }

      return this.findReferenceContentRect(element, targetRect) ?? rect
    },
    findControlContentRect(element: HTMLElement, hostRect, targetRect) {
      const candidates = Array.from(element.querySelectorAll([
        ':scope > *',
        'input',
        'button',
        'select',
        'textarea',
        '[contenteditable="true"]',
        '[role="button"]',
        '[role="textbox"]',
        '[role="combobox"]',
        '[role="listbox"]'
      ].join(','))) as HTMLElement[]

      const visibleRects = candidates
        .filter(candidate => !candidate.closest(`.${OVERLAY_CLASS}`))
        .filter(candidate => !candidate.matches('style, script, template'))
        .filter(candidate => !candidate.matches(STRUCTURAL_SELECTOR) || candidate === element)
        .map(candidate => candidate.getBoundingClientRect())
        .filter(rect => this.isVisibleRect(rect, targetRect))
        .filter(rect => this.isMeaningfulControlContentRect(rect, hostRect))

      return this.unionRects(visibleRects)
    },
    isMeaningfulControlContentRect(rect, hostRect) {
      const roundedSame = Math.round(rect.top) === Math.round(hostRect.top)
        && Math.round(rect.left) === Math.round(hostRect.left)
        && Math.round(rect.width) === Math.round(hostRect.width)
        && Math.round(rect.height) === Math.round(hostRect.height)

      if (!roundedSame) {
        return true
      }

      return rect.width < hostRect.width || rect.height < hostRect.height
    },
    findReferenceContentRect(element: HTMLElement, targetRect) {
      const candidates = Array.from(element.querySelectorAll([
        '.ae-control',
        '.ae-list',
        '.ae-query',
        'input',
        'button',
        'select',
        'textarea',
        '[contenteditable="true"]'
      ].join(','))) as HTMLElement[]

      const visibleRects = candidates
        .filter(candidate => !candidate.closest(`.${OVERLAY_CLASS}`))
        .map(candidate => candidate.getBoundingClientRect())
        .filter(rect => this.isVisibleRect(rect, targetRect))

      return this.unionRects(visibleRects)
    },
    unionRects(rects) {
      if (!rects.length) {
        return null
      }

      const top = Math.min(...rects.map(rect => rect.top))
      const left = Math.min(...rects.map(rect => rect.left))
      const right = Math.max(...rects.map(rect => rect.right))
      const bottom = Math.max(...rects.map(rect => rect.bottom))

      return {
        top,
        left,
        right,
        bottom,
        width: right - left,
        height: bottom - top
      }
    },
    isVisibleRect(rect: DOMRect, targetRect) {
      if (rect.width <= 0 || rect.height <= 0) {
        return false
      }

      if (rect.bottom < targetRect.top || rect.right < targetRect.left) {
        return false
      }

      if (rect.top > targetRect.top + targetRect.height || rect.left > targetRect.left + targetRect.width) {
        return false
      }

      return true
    },
    drill(group: WireframeGroup, event: MouseEvent) {
      if (this.isPassthroughMouseEvent(event)) {
        event.preventDefault()
        event.stopPropagation()
        this.clickThrough(event)
        return
      }

      event.preventDefault()
      event.stopPropagation()

      if (event.shiftKey) {
        this.closeGroupPath(group)
      } else {
        this.openGroupIds = new Set(this.getGroupPathIds(group))
      }

      this.rebuildGroups()
    },
    inspectGroup(group: WireframeGroup, event: MouseEvent) {
      event.preventDefault()
      event.stopPropagation()

      const inspectionPath = this.getGroupInspectionPath(group)
      if (!inspectionPath.length) {
        return
      }

      this.openGroupIds = new Set(this.getGroupPathIds(group))
      this.rebuildGroups()

      if (this.Aeppic.Developer?.setInspectorDiscoveryPath) {
        this.Aeppic.Developer.setInspectorDiscoveryPath(inspectionPath, this.getGroupInspectionElements(group), {
          source: 'wireframe'
        })
      }
    },
    getGroupInspectionPath(group: WireframeGroup) {
      const groupById = new Map((this.groups as WireframeGroup[]).map(group => [group.id, group]))
      const pathIds = this.getGroupPathIds(group)
      const path = []

      for (const groupId of pathIds) {
        const pathGroup = groupById.get(groupId)
        if (!pathGroup) {
          continue
        }

        for (const label of pathGroup.labels) {
          if (label?.meta) {
            path.push(label.meta)
          }
        }
      }

      return path
    },
    getGroupInspectionElements(group: WireframeGroup) {
      const groupById = new Map((this.groups as WireframeGroup[]).map(group => [group.id, group]))
      const elements = []

      for (const groupId of this.getGroupPathIds(group)) {
        const pathGroup = groupById.get(groupId)
        if (!pathGroup) {
          continue
        }

        for (const elementId of pathGroup.elementIds) {
          const element = (this.measuredElementMap as Map<string, HTMLElement>).get(elementId)
          if (element) {
            elements.push(element)
          }
        }
      }

      return elements
    },
    clickThrough(event: MouseEvent) {
      const elements = document.elementsFromPoint(event.clientX, event.clientY) as HTMLElement[]
      const target = elements.find(element => !element.closest?.(`.${OVERLAY_CLASS}`))

      if (!target) {
        return
      }

      target.dispatchEvent(new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
        clientX: event.clientX,
        clientY: event.clientY,
        screenX: event.screenX,
        screenY: event.screenY,
        ctrlKey: event.ctrlKey,
        shiftKey: event.shiftKey,
        altKey: event.altKey,
        metaKey: event.metaKey,
        button: event.button,
        buttons: event.buttons
      }))
    },
    closeGroupPath(group: WireframeGroup) {
      const pathIds = this.getGroupPathIds(group)
      const deepestOpenGroupId = pathIds
        .slice()
        .reverse()
        .find(groupId => (this.openGroupIds as Set<string>).has(groupId))

      if (!deepestOpenGroupId) {
        return
      }

      const openGroups = this.groups as WireframeGroup[]
      const descendantIds = new Set<string>()
      const collectDescendants = (groupId: string) => {
        const current = openGroups.find(candidate => candidate.id === groupId)
        if (!current) {
          return
        }

        for (const childId of current.childIds) {
          descendantIds.add(childId)
          collectDescendants(childId)
        }
      }

      collectDescendants(deepestOpenGroupId)

      this.openGroupIds = new Set(Array.from(this.openGroupIds as Set<string>).filter(groupId => {
        return groupId !== deepestOpenGroupId && !descendantIds.has(groupId)
      }))
    },
    getGroupPathIds(group: WireframeGroup) {
      const groupById = new Map((this.groups as WireframeGroup[]).map(group => [group.id, group]))
      const path: string[] = []
      let current: WireframeGroup | undefined = group

      while (current) {
        path.unshift(current.id)
        current = current.parentId ? groupById.get(current.parentId) : undefined
      }

      return path
    },
    getDeepestOpenGroupId(groups: WireframeGroup[]) {
      const openGroupIds = this.openGroupIds as Set<string>
      const openGroups = groups.filter(group => openGroupIds.has(group.id))

      if (!openGroups.length) {
        return null
      }

      return openGroups.sort((a, b) => b.depth - a.depth)[0].id
    },
    isGroupInFocusBranch(group: WireframeGroup, deepestOpenGroupId: string | null, groupById: Map<string, WireframeGroup>) {
      if (!deepestOpenGroupId) {
        return true
      }

      return group.id === deepestOpenGroupId
        || this.isAncestorGroup(group.id, deepestOpenGroupId, groupById)
        || this.isAncestorGroup(deepestOpenGroupId, group.id, groupById)
    },
    isAncestorGroup(ancestorId: string, descendantId: string, groupById: Map<string, WireframeGroup>) {
      let current = groupById.get(descendantId)

      while (current?.parentId) {
        if (current.parentId === ancestorId) {
          return true
        }

        current = groupById.get(current.parentId)
      }

      return false
    },
    onKeydown(event: KeyboardEvent) {
      if (!this.isEnabled()) {
        return
      }

      if (event.key === this.passthroughModifierKey) {
        this.passthroughMode = true
        return
      }

      if (event.key === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        this.Aeppic.Developer?.disableWireframeMode?.()
        return
      }

      if (this.isEditableKeyboardTarget(event.target)) {
        return
      }

      if (event.key === '+' || event.key === '=') {
        event.preventDefault()
        event.stopPropagation()
        this.localDepth += 1
        this.openGroupIds.clear()
        this.rebuildGroups()
      } else if (event.key === '-' || event.key === '_') {
        event.preventDefault()
        event.stopPropagation()
        this.localDepth = Math.max(0, this.localDepth - 1)
        this.openGroupIds.clear()
        this.rebuildGroups()
      }
    },
    onKeyup(event: KeyboardEvent) {
      if (event.key === this.passthroughModifierKey) {
        this.disablePassthroughMode()
      }
    },
    onPointerModifierChange(event: PointerEvent) {
      this.passthroughMode = this.isPassthroughMouseEvent(event)
    },
    onContextMenu(event: MouseEvent) {
      if (!this.isEnabled() || !this.isPassthroughMouseEvent(event)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
    },
    onVisibilityChange() {
      if (document.hidden) {
        this.disablePassthroughMode()
      }
    },
    disablePassthroughMode() {
      this.passthroughMode = false
    },
    getPassthroughModifierKey() {
      return this.isMacPlatform() ? 'Meta' : 'Control'
    },
    isPassthroughMouseEvent(event: MouseEvent | PointerEvent) {
      return this.passthroughModifierKey === 'Meta' ? event.metaKey === true : event.ctrlKey === true
    },
    isMacPlatform() {
      const platform = (navigator as any).userAgentData?.platform || navigator.platform || ''
      return /Mac|iPhone|iPad|iPod/i.test(platform)
    },
    isEditableKeyboardTarget(target: EventTarget | null) {
      const element = target as HTMLElement | null
      if (!element) {
        return false
      }

      const tagName = element.tagName?.toLowerCase()
      return element.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select'
    },
    formatLabel(label: WireframeLabel) {
      const parts = [label.type]
      if (label.name) parts.push(String(label.name))
      if (label.fieldName) parts.push(`field:${label.fieldName}`)
      if (label.id) parts.push(String(label.id).slice(0, 8))
      return parts.join(' ')
    },
    formatGroupLabel(group: WireframeGroup) {
      return group.labels.map(this.formatLabel).join(' / ')
    },
    truncateSvgLabel(text: string) {
      const maxLength = 72
      return text.length > maxLength ? `${text.slice(0, maxLength - 1)}…` : text
    },
    estimateSvgLabelWidth(text: string) {
      return Math.min(360, Math.max(36, text.length * 6.4 + 10))
    },
    isCompactGroup(group: WireframeGroup, labelWidth: number, navButtonWidth: number) {
      return group.rect.height < 56 || group.rect.width < labelWidth + navButtonWidth + 12
    },
    getLabelPosition(group: WireframeGroup, labelWidth: number, navButtonWidth: number, compact: boolean) {
      const labelHeight = 18
      const labelGap = 6
      const fullLabelWidth = labelWidth + navButtonWidth
      const maxX = Math.max(0, this.targetRect.width - fullLabelWidth)
      const x = Math.min(Math.max(0, group.rect.left), maxX)

      if (!compact) {
        return {
          x,
          y: Math.max(0, group.rect.top + group.labelStackIndex * labelHeight)
        }
      }

      const canPlaceAbove = group.rect.top >= labelHeight + labelGap
      const y = canPlaceAbove
        ? group.rect.top - labelHeight - labelGap
        : Math.min(
          Math.max(0, this.targetRect.height - labelHeight),
          group.rect.top + group.rect.height + labelGap
        )

      return { x, y }
    }
  },
  render(createElement) {
    if (!this.enabled || !this.targetRect) {
      return null
    }

    const visibleGroups = (this.groups as WireframeGroup[]).filter(group => group.visible)
    const groupById = new Map((this.groups as WireframeGroup[]).map(group => [group.id, group]))
    const visibleGroupIds = new Set(visibleGroups.map(group => group.id))
    const deepestOpenGroupId = this.getDeepestOpenGroupId(visibleGroups)
    const isLeafGroup = (group: WireframeGroup) => !group.childIds.some(childId => visibleGroupIds.has(childId))
    const svgGroups = visibleGroups.map(group => {
      const leaf = isLeafGroup(group)
      const offPath = !this.isGroupInFocusBranch(group, deepestOpenGroupId, groupById)
      const label = this.formatGroupLabel(group)
      const visibleLabel = this.truncateSvgLabel(label)
      const labelWidth = this.estimateSvgLabelWidth(visibleLabel)
      const inspectable = this.getGroupInspectionPath(group).length > 0
      const navButtonWidth = inspectable ? 20 : 0
      const compact = this.isCompactGroup(group, labelWidth, navButtonWidth)
      const labelPosition = this.getLabelPosition(group, labelWidth, navButtonWidth, compact)

      return createElement('g', {
        key: group.id,
        class: {
          'ae-wireframe-view__group': true,
          'ae-wireframe-view__group--compact': compact
        },
        attrs: {
          'data-ae-wireframe-group-id': group.id
        },
        on: {
          click: event => this.drill(group, event)
        }
      }, [
        createElement('title', label),
        createElement('rect', {
          key: `${group.id}:box`,
          class: {
            'ae-wireframe-view__box': true,
            'ae-wireframe-view__box--open': group.open,
            'ae-wireframe-view__box--leaf': leaf,
            'ae-wireframe-view__box--ancestor': !leaf,
            'ae-wireframe-view__box--off-path': offPath
          },
          attrs: {
            x: group.rect.left,
            y: group.rect.top,
            width: group.rect.width,
            height: group.rect.height
          }
        }),
        createElement('rect', {
          key: `${group.id}:hit`,
          class: 'ae-wireframe-view__hit',
          attrs: {
            x: group.rect.left,
            y: group.rect.top,
            width: group.rect.width,
            height: group.rect.height
          }
        }),
        createElement('rect', {
          key: `${group.id}:label-bg`,
          class: {
            'ae-wireframe-view__label-bg': true,
            'ae-wireframe-view__label-bg--off-path': offPath
          },
          attrs: {
            x: labelPosition.x,
            y: labelPosition.y,
            width: labelWidth + navButtonWidth,
            height: 18,
            rx: 3,
            ry: 3
          }
        }),
        createElement('text', {
          key: `${group.id}:label`,
          class: {
            'ae-wireframe-view__label': true,
            'ae-wireframe-view__label--leaf': leaf,
            'ae-wireframe-view__label--ancestor': !leaf,
            'ae-wireframe-view__label--off-path': offPath
          },
          attrs: {
            x: labelPosition.x + 5,
            y: labelPosition.y + 3
          }
        }, visibleLabel),
        inspectable ? createElement('g', {
          key: `${group.id}:nav`,
          class: 'ae-wireframe-view__nav-action',
          attrs: {
            title: 'Open in developer inspector'
          },
          on: {
            click: event => this.inspectGroup(group, event)
          }
        }, [
          createElement('rect', {
            key: `${group.id}:nav-bg`,
            class: 'ae-wireframe-view__nav-action-bg',
            attrs: {
              x: labelPosition.x + labelWidth,
              y: labelPosition.y,
              width: navButtonWidth,
              height: 18,
              rx: 3,
              ry: 3
            }
          }),
          createElement('text', {
            key: `${group.id}:nav-text`,
            class: 'ae-wireframe-view__nav-action-text',
            attrs: {
              x: labelPosition.x + labelWidth + navButtonWidth / 2,
              y: labelPosition.y + 9
            }
          }, '›')
        ]) : null
      ])
    })

    const svg = createElement('svg', {
      class: 'ae-wireframe-view__svg',
      attrs: {
        width: this.targetRect.width,
        height: this.targetRect.height,
        viewBox: `0 0 ${this.targetRect.width} ${this.targetRect.height}`
      }
    }, svgGroups)

    return createElement('div', {
      class: {
        [OVERLAY_CLASS]: true,
        'ae-wireframe-view--passthrough': this.passthroughMode
      },
      attrs: {
        'data-ae-wireframe-overlay': 'true'
      },
      style: {
        top: `${this.targetRect.top}px`,
        left: `${this.targetRect.left}px`,
        width: `${this.targetRect.width}px`,
        height: `${this.targetRect.height}px`
      }
    }, [svg])
  }
}
